import { useHistory, useLocation } from 'react-router-dom';
import { COMPANY_QUERY_PARAMS } from 'features/inventory-profiles/constants';
import difference from 'lodash/difference';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import {
  canUserOrStaffEditEntity,
  Permissions,
  userHasPermission,
  userHasRole
} from 'services/Authorization';
import { FormFieldsMap, FormFieldsMapKeys } from 'UI/components/organisms/CompanyForm/fields';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { navigateToSendout } from 'UI/pages/Sendouts/utils';

const FIELDS_EDITABLE_IN_IMPORTED_COMPANIES = [
  FormFieldsMap.Specialty.key,
  FormFieldsMap.Specialties.key,
  FormFieldsMap.Subspecialty.key,
  FormFieldsMap.Subspecialties.key,
  FormFieldsMap.State.key,
  FormFieldsMap.City.key,
  FormFieldsMap.Zip.key,
  FormFieldsMap.Address.key,
  FormFieldsMap.Phone.key,
  FormFieldsMap.Ext.key,
  FormFieldsMap.Phones.key,
  FormFieldsMap.Website.key,
  FormFieldsMap.LinkedinUrl.key,
  FormFieldsMap.Email.key
];

/**
 * What the current user may see or do on a Company profile.
 * The only place of the Company profile that reads the user, the feature flags and the
 * permission helpers. Returns named booleans.
 * @param {Object} options
 * @param {Object} options.company Company profile
 * @param {boolean} [options.isAlreadyAccountable] The user is already the recruiter: nothing to claim
 * @param {Array<{ id: number }> | null} [options.recruiterRoles] Roles of the assigned recruiter, from
 *   useCompanyProfile. `null` when they failed to load: the Data Coordinator rule is unknown, so
 *   editing is turned off rather than every field unlocked
 * @returns {Object} Named booleans
 */
export const useCompanyPermissions = ({
  company,
  isAlreadyAccountable = false,
  recruiterRoles = []
}) => {
  const [user] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const areRecruiterRolesUnknown = recruiterRoles === null;
  const isRecruiterDataCoordinator = Boolean(
    recruiterRoles?.some(({ id }) => id === Roles.DataCoordinator)
  );
  const isEditionCheckIgnored = isRecruiterDataCoordinator && !userHasRole(Roles.DataCoordinator);

  return {
    canClaimAccountability: !isAlreadyAccountable,
    canEdit:
      !areRecruiterRolesUnknown &&
      (canUserOrStaffEditEntity(user, company) || isEditionCheckIgnored),
    canUseDailyPlan:
      checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanAddTaskFromProfile) &&
      userHasPermission(Permissions.DailyPlan.CanUseModule),
    canUpdateCompanyType: Boolean(company.companyTypeReassure?.recruiterCanRequest),
    canSeeAssociatedCompanies: checkIfFeatureFlagEnabled(FeatureFlags.LinkCompanies),
    canSeeSalesTab: checkIfFeatureFlagEnabled(FeatureFlags.CompaniesSalesTab),
    disabledEditKeys: isEditionCheckIgnored
      ? difference(FormFieldsMapKeys, FIELDS_EDITABLE_IN_IMPORTED_COMPANIES)
      : []
  };
};

/**
 * Sign status drawer driven by the follow-up URL params; `dismiss` removes them.
 * @returns {{ followUpId: string | null, isOpen: boolean, dismiss: () => void }}
 */
export const useSignStatusFromUrl = () => {
  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const followUpId = query.get(COMPANY_QUERY_PARAMS.FollowUpId);

  const dismiss = () => {
    query.delete(COMPANY_QUERY_PARAMS.FollowUpId);
    query.delete(COMPANY_QUERY_PARAMS.ShowOpsVerificationModal);
    history.replace({ ...location, search: query.toString() });
  };

  return {
    followUpId,
    isOpen: Boolean(followUpId && query.get(COMPANY_QUERY_PARAMS.ShowOpsVerificationModal)),
    dismiss
  };
};

/**
 * Navigation out of the Company profile.
 * @returns {{ openDailyPlan: () => void, navigateFromSendout: Function }}
 */
export const useCompanyNavigation = () => {
  const history = useHistory();

  return {
    navigateFromSendout: navigateToSendout(history),
    openDailyPlan: () => history.push(EntityRoutes.DailyPlan)
  };
};
